import type { SEOMetadataProps } from '@src/components/sco/utilities/SEOMetadata'
import { MemberProfileStatusProps, memberProfileCanonicalUrl } from '@src/lib/memberProfileStatus'
import { seoDefaults } from '@src/seo-defaults'

export type MemberProfileSeoUser = {
  fullName?: string | null
  displayName?: string | null
  shortBio?: string | null
  bio?: string | null
  title?: string | null
  pictureUrl?: string | null
  slug?: string | null
  advisorProfile?: { headline?: string | null } | null
  /** Combined public review average + count (L46). Drives `aggregateRating` from 1 review (L41). */
  sessionReviewSummary?: { averageScore?: number | null; count?: number | null } | null
}

const DESCRIPTION_MAX = 160

/** Plain one-line text for meta tags: drop markdown marks and collapse whitespace. */
const plainText = (value?: string | null): string =>
  (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[*_`#>[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const truncate = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value

/**
 * schema.org `AggregateRating` for the 1–5 star session reviews, or null when there is no review
 * or no usable average. Values are plain numbers (no user text), so nothing extra to escape.
 */
export const memberProfileAggregateRating = (
  summary: MemberProfileSeoUser['sessionReviewSummary']
): Record<string, unknown> | null => {
  const count = Number(summary?.count ?? 0)
  const average = summary?.averageScore == null ? NaN : Number(summary.averageScore)
  if (!Number.isFinite(count) || count < 1 || !Number.isFinite(average) || average < 1 || average > 5) {
    return null
  }
  return {
    '@type': 'AggregateRating',
    ratingValue: Math.round(average * 100) / 100,
    reviewCount: Math.floor(count),
    bestRating: 5,
    worstRating: 1,
  }
}

/**
 * SEO for a member profile at `/members/[slug]` and its aliases (`/members/profile/[slug]`,
 * `/founders/[slug]`, `/advisors/profile/[slug]`, id URLs) — L5/L6: every alias canonicals to
 * `/members/{slug}`, no redirects.
 *
 * Robots: `noindex` unless the API says `isIndexable` (public + approved advisor). A missing or
 * failed status read is treated as not indexable. JSON-LD `Person` only on indexable profiles and
 * never carries pricing (L1); it carries `aggregateRating` once there is at least one review (L41).
 * Outside production `seoDefaults.noFollow` keeps `nofollow`.
 */
export const memberProfileSeo = ({
  status,
  user,
}: {
  status: MemberProfileStatusProps | null | undefined
  user: MemberProfileSeoUser | null | undefined
}): SEOMetadataProps => {
  const noFollow = !!seoDefaults.noFollow

  if (status?.status === 'NOT_FOUND' || status?.status === 'UNAVAILABLE') {
    return { title: 'Profile unavailable', noIndex: true, noFollow }
  }

  const slug = (status?.status === 'VISIBLE' && status.slug) || user?.slug || ''
  if (!user || !slug) {
    return { noIndex: true, noFollow }
  }

  const name = plainText(user.fullName || user.displayName) || 'Member'
  const headline = plainText(user.advisorProfile?.headline || user.title)
  const isIndexable = status?.status === 'VISIBLE' && !!status.isIndexable
  const aggregateRating = isIndexable ? memberProfileAggregateRating(user.sessionReviewSummary) : null
  const url = memberProfileCanonicalUrl(slug)
  const description = truncate(
    plainText(user.shortBio) || headline || plainText(user.bio) || `${name} on Startups.com`,
    DESCRIPTION_MAX
  )

  return {
    title: headline ? `${name} · ${truncate(headline, 70)}` : name,
    description,
    imageUrl: user.pictureUrl || undefined,
    type: 'profile',
    url,
    noIndex: !isIndexable,
    noFollow: !isIndexable && noFollow,
    jsonLd: isIndexable
      ? {
          '@context': 'https://schema.org',
          '@type': 'Person',
          name,
          url,
          ...(user.pictureUrl ? { image: user.pictureUrl } : {}),
          ...(headline ? { jobTitle: headline } : {}),
          ...(plainText(user.shortBio) ? { description } : {}),
          ...(aggregateRating ? { aggregateRating } : {}),
        }
      : null,
  }
}
